import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { useEffect, ReactNode } from 'react'

import { endRequest, receiveErrorAlerts, startRequest } from '@/actions'
import config from '@/config'
import { ERROR_MESSAGE_UNKNOWN } from '@/constants'
import { SilentCustomRequestError } from '@/helpers/requestErrors'
import { sendApiErrorsToSentry } from '@/helpers/sentry'
import { useAppDispatch } from '@/store'

import { useResetSessionAndLogout } from './auth/hooks/use-reset-session-and-logout'

import { getAccessToken } from './auth/get-access-token'

class MissingTokenError extends Error {
    name = 'MissingTokenError'
}

const getShouldSendErrorToSentry = (axiosError: AxiosError) => {
    const status = axiosError?.response?.status

    // Log all 400 & 5XX response errors to Sentry
    return status === 400 || String(status)[0] === '5'
}

const getIsUnauthorizedRequest = (error: AxiosError | MissingTokenError) => {
    if (error instanceof MissingTokenError) return true

    const status = error?.response?.status
    const url = error?.config?.url

    return status === 401 && url !== '/user/loggedin'
}

const getIsPublicEndpoint = (requestConfig: AxiosRequestConfig) => {
    const isMagicLinkEndpoint = !!requestConfig?.url?.includes('magiclink/') && requestConfig?.method === 'get'
    return isMagicLinkEndpoint
}

let responseInterceptorInstance: number
let requestInterceptorInstance: number

export const InterceptorProvider = ({ children }: { children: ReactNode }) => {
    const dispatch = useAppDispatch()

    const { resetSessionAndLogout } = useResetSessionAndLogout()

    useEffect(() => {
        // Cleans up previous interceptor on re-render
        if (requestInterceptorInstance !== undefined) axios.interceptors.request.eject(requestInterceptorInstance)

        requestInterceptorInstance = axios.interceptors.request.use(async (requestConfig) => {
            dispatch(startRequest())

            if (requestConfig.baseURL === config.apiUrl) {
                if (getIsPublicEndpoint(requestConfig)) return requestConfig

                const token = await getAccessToken()

                if (!token) {
                    resetSessionAndLogout()
                    throw new MissingTokenError()
                }

                const requestConfigWithToken = { ...requestConfig }
                requestConfigWithToken.headers.Authorization = `Bearer ${token}`

                return requestConfigWithToken
            }

            return requestConfig
        })

        if (responseInterceptorInstance !== undefined) axios.interceptors.response.eject(responseInterceptorInstance)

        responseInterceptorInstance = axios.interceptors.response.use(
            (response) => {
                dispatch(endRequest())
                return response
            },
            (error: AxiosError) => {
                dispatch(endRequest())

                if (getShouldSendErrorToSentry(error)) {
                    sendApiErrorsToSentry(error)
                }

                if (getIsUnauthorizedRequest(error)) {
                    resetSessionAndLogout()
                    return Promise.reject(new SilentCustomRequestError())
                }

                // If there is no error.response trigger an alert
                if (!error?.response) {
                    dispatch(receiveErrorAlerts(ERROR_MESSAGE_UNKNOWN))
                }

                return Promise.reject(error)
            }
        )
    }, [dispatch, resetSessionAndLogout])

    return <>{children}</>
}
